import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import type { Page, PlatStatisticAnalysisPagingVo } from "@zhihaoscm/entity";
import { Alert, Button, Card, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Select as ZhSelect,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import { DEFAULT_VALUE } from "@/config";
import { GET_ORDER_STATE, PLAT_SHIP_NEED_STATE } from "@/meta/logistics/shipNeed";
import { useUserStore } from "@/stores/react/authStore";
import { dateFormat } from "@/utils";
import {
    ShipNeedExposureVisitorsDialog,
    type ShipNeedExposureVisitorsDialogProps
} from "./ShipNeedExposureVisitorsDialog";
import { ShipNeedExposureVisitsDialog, type ShipNeedExposureVisitsDialogProps } from "./ShipNeedExposureVisitsDialog";

export interface ShipNeedExposureQuery {
    page: number;
    size: number;
    scope?: number;
    beginTime?: string;
    endTime?: string;
    platId?: string;
    sourcePortName?: string;
    destinationPortName?: string;
    starLoadTime?: string;
    endLoadTime?: string;
    orderAcceptState?: number;
    state?: number;
}

export type ShipNeedExposureRow = Partial<PlatStatisticAnalysisPagingVo>;

export interface ShipNeedExposureAnalysisProps {
    timeParams: Omit<Partial<ShipNeedExposureQuery>, "page" | "size">;
    loadRows?: (query: ShipNeedExposureQuery) => Promise<Page<ShipNeedExposureRow>>;
    exportRows?: (query: Record<string, unknown>, filename: string) => Promise<unknown>;
    permissions?: string[];
    loadVisitors?: ShipNeedExposureVisitorsDialogProps["loadRows"];
    exportVisitors?: ShipNeedExposureVisitorsDialogProps["exportRows"];
    loadVisits?: ShipNeedExposureVisitsDialogProps["loadRows"];
    exportVisits?: ShipNeedExposureVisitsDialogProps["exportRows"];
}

interface ExposureFilterState {
    platId: string;
    sourcePortName: string;
    destinationPortName: string;
    date: [string, string] | null;
    orderAcceptState?: number;
    state?: number;
}

/** 需求曝光分析 API adapter，沿用旧 Vue analysis/plat/paging 接口。 */
export async function loadShipNeedExposureRows(query: ShipNeedExposureQuery) {
    const { analysisPlatService } = await import("@/api/services/analysisPlatService");
    return analysisPlatService.getAnalysisPlatPaging(query) as Promise<Page<ShipNeedExposureRow>>;
}

/** 需求曝光分析导出 API adapter，保留旧 Vue exportType.plat=1。 */
export async function exportShipNeedExposureRows(query: Record<string, unknown>, filename: string) {
    const { analysisPlatService } = await import("@/api/services/analysisPlatService");
    return analysisPlatService.getAnalysisPlatExport(query, filename);
}

const text = (value: unknown) => (value == null || value === "" ? DEFAULT_VALUE : String(value));
const enumText = (meta: { getIntroByCode: (code?: number | string) => string }, value: unknown) =>
    value == null || value === "" ? DEFAULT_VALUE : meta.getIntroByCode(Number(value)) || DEFAULT_VALUE;

const defaultFilters: ExposureFilterState = {
    platId: "",
    sourcePortName: "",
    destinationPortName: "",
    date: null,
    orderAcceptState: undefined,
    state: undefined
};

/** 旧 Vue 曝光列表列集合，暂不包含后续独立迁移的访问明细弹窗。 */
const getExposureColumns = (
    openVisitors: (platId: string) => void,
    openVisits: (platId: string) => void
): TableColumnProps<ShipNeedExposureRow>[] => [
    { key: "index", label: "编号", width: 80, render: ({ rowIndex }) => rowIndex + 1 },
    {
        key: "plat.id",
        label: "需求编号",
        width: 150,
        render: ({ row }) => {
            const id = row.plat?.id;
            return id ? (
                <Link to={`/logistics/shippingNeed/platformShipNeedInfo/${id}?from=shipNeedAnalysis`}>
                    {String(id)}
                </Link>
            ) : (
                text(id)
            );
        }
    },
    { key: "plat.sourcePortName", label: "起始地", minWidth: 148, render: ({ row }) => text(row.plat?.sourcePortName) },
    {
        key: "plat.destinationPortName",
        label: "目的地",
        minWidth: 148,
        render: ({ row }) => text(row.plat?.destinationPortName)
    },
    {
        key: "plat.loadDate",
        label: "预期装卸日期",
        width: 148,
        render: ({ row }) =>
            row.plat?.loadDate == null
                ? DEFAULT_VALUE
                : `${dateFormat(String(row.plat.loadDate))} +${text(row.plat.loadDays)}天`
    },
    {
        key: "plat.orderAcceptanceState",
        label: "报价状态",
        width: 92,
        render: ({ row }) => enumText(GET_ORDER_STATE, row.plat?.orderAcceptanceState)
    },
    {
        key: "plat.state",
        label: "需求状态",
        width: 92,
        render: ({ row }) => enumText(PLAT_SHIP_NEED_STATE, row.plat?.state)
    },
    {
        key: "exposureNum",
        label: "需求曝光人数",
        width: 200,
        render: ({ row }) =>
            row.exposureNum ? (
                <Button type="link" onClick={() => row.plat?.id && openVisitors(String(row.plat.id))}>
                    {row.exposureNum}
                </Button>
            ) : (
                text(row.exposureNum)
            )
    },
    {
        key: "exposureTimes",
        label: "需求曝光次数",
        width: 200,
        render: ({ row }) =>
            row.exposureTimes ? (
                <Button type="link" onClick={() => row.plat?.id && openVisits(String(row.plat.id))}>
                    {row.exposureTimes}
                </Button>
            ) : (
                text(row.exposureTimes)
            )
    },
    { key: "avgExposureTimes", label: "人均访问次数", width: 200, render: ({ row }) => text(row.avgExposureTimes) }
];

/** 船运需求曝光分析主体列表，按父页面时间范围重置为第一页。 */
export function ShipNeedExposureAnalysis({
    timeParams,
    loadRows = loadShipNeedExposureRows,
    exportRows = exportShipNeedExposureRows,
    permissions,
    loadVisitors,
    exportVisitors,
    loadVisits,
    exportVisits
}: ShipNeedExposureAnalysisProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 保持旧 Vue v-auth 语义：曝光列表导出仅对指定权限开放。
    const canExport = (permissions ?? storePermissions).includes("ROLE_DEMAND_CONVERSION_EXPORT");
    const [query, setQuery] = useState<ShipNeedExposureQuery>(() => ({ page: 1, size: 10, ...timeParams }));
    const [filters, setFilters] = useState<ExposureFilterState>(defaultFilters);
    const [rows, setRows] = useState<ShipNeedExposureRow[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [visitorOpen, setVisitorOpen] = useState(false);
    const [visitorPlatId, setVisitorPlatId] = useState("");
    const [visitOpen, setVisitOpen] = useState(false);
    const [visitPlatId, setVisitPlatId] = useState("");

    useEffect(() => {
        setQuery(current => ({ page: 1, size: current.size, ...timeParams }));
        setFilters(defaultFilters);
    }, [timeParams.beginTime, timeParams.endTime, timeParams.scope]);

    /** 将旧 Vue 曝光列表筛选字段转换成 analysis/plat/paging 参数。 */
    const submitFilters = () => {
        const next: ShipNeedExposureQuery = { page: 1, size: query.size, ...timeParams };
        if (filters.platId.trim()) next.platId = filters.platId.trim();
        if (filters.sourcePortName.trim()) next.sourcePortName = filters.sourcePortName.trim();
        if (filters.destinationPortName.trim()) next.destinationPortName = filters.destinationPortName.trim();
        if (filters.date?.[0] && filters.date[1]) {
            next.starLoadTime = dateFormat(filters.date[0]);
            next.endLoadTime = dateFormat(filters.date[1]);
        }
        if (filters.orderAcceptState !== undefined) next.orderAcceptState = filters.orderAcceptState;
        if (filters.state !== undefined) next.state = filters.state;
        setQuery(next);
    };

    /** 清空旧 Vue 曝光列表筛选并重新请求第一页。 */
    const resetFilters = () => {
        setFilters(defaultFilters);
        setQuery({ page: 1, size: 10, ...timeParams });
    };

    /** 打开指定需求的访问用户明细。 */
    const openVisitors = (platId: string) => {
        setVisitorPlatId(platId);
        setVisitorOpen(true);
    };

    /** 打开指定需求的访问次数明细。 */
    const openVisits = (platId: string) => {
        setVisitPlatId(platId);
        setVisitOpen(true);
    };

    /** 沿用旧 Vue 导出参数，确认后去掉 page 并用当前总数覆盖 size。 */
    const confirmExport = async () => {
        const exportQuery: Record<string, unknown> = { ...query, size: total, type: 1 };
        delete exportQuery.page;
        setExporting(true);
        try {
            await exportRows(exportQuery, "船运需求曝光分析");
            setExportOpen(false);
        } catch {
            setError("船运需求曝光分析导出失败");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        let disposed = false;
        void loadRows(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("船运需求曝光分析加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRows, query]);

    return (
        <Card
            title="船运需求曝光分析"
            extra={
                canExport ? (
                    <Button type="primary" aria-label="导出船运需求曝光分析" onClick={() => setExportOpen(true)}>
                        导出
                    </Button>
                ) : null
            }
        >
            <ZhTable<ShipNeedExposureRow>
                rowKey={row => String(row.plat?.id || "ship-need-exposure")}
                data={rows}
                columns={getExposureColumns(openVisitors, openVisits)}
                pagination={false}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        {error ? <Alert role="alert" type="error" message={error} /> : null}

                        {/* 曝光明细筛选接入统一查询栏，确保日期范围宽度全局一致。 */}
                        <Space wrap className="admin-table-filter-form mb-4">
                            <Input
                                aria-label="需求编号"
                                placeholder="请输入需求编号搜索"
                                value={filters.platId}
                                onChange={event => setFilters(current => ({ ...current, platId: event.target.value }))}
                            />
                            <Input
                                aria-label="起始地"
                                placeholder="请输入始发地搜索"
                                value={filters.sourcePortName}
                                onChange={event =>
                                    setFilters(current => ({ ...current, sourcePortName: event.target.value }))
                                }
                            />
                            <Input
                                aria-label="目的地"
                                placeholder="请输入目的地搜索"
                                value={filters.destinationPortName}
                                onChange={event =>
                                    setFilters(current => ({ ...current, destinationPortName: event.target.value }))
                                }
                            />
                            <ZhDateRangePicker
                                aria-label="预期装卸日期"
                                value={filters.date}
                                valueFormat="YYYY-MM-DD"
                                format="YYYY-MM-DD"
                                rangeSeparator="-"
                                startPlaceholder="装卸开始时间"
                                endPlaceholder="装卸结束时间"
                                onChange={(_value, formattedValue) =>
                                    setFilters(current => ({ ...current, date: formattedValue }))
                                }
                            />
                            <ZhSelect
                                aria-label="报价状态"
                                allowClear
                                placeholder="请选择报价状态"
                                value={filters.orderAcceptState}
                                options={GET_ORDER_STATE.options
                                    .filter(option => option.value === GET_ORDER_STATE.PENDING_PROCESSING.code)
                                    .map(option => ({ label: option.label, value: Number(option.value) }))}
                                onChange={value =>
                                    setFilters(current => ({
                                        ...current,
                                        orderAcceptState: value as number | undefined
                                    }))
                                }
                            />
                            <ZhSelect
                                aria-label="需求状态"
                                allowClear
                                placeholder="请选择需求状态"
                                value={filters.state}
                                options={PLAT_SHIP_NEED_STATE.options.map(option => ({
                                    label: option.label,
                                    value: Number(option.value)
                                }))}
                                onChange={value =>
                                    setFilters(current => ({ ...current, state: value as number | undefined }))
                                }
                            />
                            <Button type="primary" aria-label="查询" onClick={submitFilters}>
                                查询
                            </Button>
                            <Button aria-label="重置" onClick={resetFilters}>
                                重置
                            </Button>
                        </Space>
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            hideOnSinglePage
                            onChange={(page, pageSize) =>
                                setQuery(current => ({
                                    ...current,
                                    // 切换每页条数后回到第一页，普通翻页保留当前页。
                                    page: pageSize !== current.size ? 1 : page,
                                    size: pageSize
                                }))
                            }
                        />
                    </div>
                }
            />
            <ZhDialog
                open={exportOpen}
                title="导出"
                width={400}
                closeOnClickModal={false}
                onCancel={() => setExportOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setExportOpen(false)}>取消</Button>
                        <Button type="primary" loading={exporting} onClick={() => void confirmExport()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                是否导出当前数据？
            </ZhDialog>
            <ShipNeedExposureVisitorsDialog
                open={visitorOpen}
                platId={visitorPlatId}
                onClose={() => setVisitorOpen(false)}
                loadRows={loadVisitors}
                exportRows={exportVisitors}
                permissions={permissions}
            />
            <ShipNeedExposureVisitsDialog
                open={visitOpen}
                platId={visitPlatId}
                onClose={() => setVisitOpen(false)}
                loadRows={loadVisits}
                exportRows={exportVisits}
                permissions={permissions}
            />
        </Card>
    );
}
